// The conservative depth buffer (APP_VISIBILITY §3b / §3c, P2 cdb-cull):
// a unit is skipped only when it is PROVABLY hidden — every ray from every
// eye of the key's bound towards any point of the unit's upper-bound box is
// blocked by geometry that is provably drawn nearer. Pure (no three import):
// scripts/check-vis.mjs runs it under node against a brute-force truth.
//
//   LowerGround   a LOWER bound of the drawn ground per patch (owner D1-a; the
//                 client ground's GM L0 node boxes since rearch-r8k, 128 m;
//                 one patch = the min of its four samples, which bounds the
//                 ground from below) + its min-mip, and a
//                 mask: the bore cut's holes (the plan AABBs of the volumes the
//                 bake cut the ground tree with, tested exactly where a patch
//                 is flagged) and everything off the ground tree's frame (the
//                 ring is no bound).
//   HorizonTable  rotation-free, per eye CELL (±CDB.half in x / z, y up to
//                 CDB.yUp over the cell's eye): per azimuth bin and horizontal
//                 reach D_j, the tangent t below which every ray from every
//                 eye of the cell is blocked by the ground within D_j. Built
//                 over arc sectors widened by the cell (asin(half·√2 / ρ)), the
//                 ground lowered by the drawn nodes' measured undershoot M(ρ);
//                 a sector that meets a hole or the frame edge freezes its bin
//                 (no hole between the eye and the crossing); a hole inside
//                 the first radius freezes the bins whose rays from the cell
//                 can reach it, one under the cell itself the whole table.
//                 Sliced (step(ms)); used only once complete.
//   ConservativeDepth  cells over the key's DILATED view rectangle (tangent
//                 space of the key camera: every pose of the bound sees a
//                 subset of it); per cell zCover = a distance by which every
//                 ray of the cell from every eye of the box is blocked (+∞ =
//                 not covered). Terrain via the horizon table (the cell's cone:
//                 centre ± its corner radius), buildings via the inset walls
//                 of collision prisms (all four corner rays inside ONE wall,
//                 shrunk by the eye-box slack |h|(1 + 1 / |n·d|)).
//   hidden(lo, hi) the unit box grown by the eye box's half extent, clipped to
//                 the front of the eye, projected from the box centre: hidden
//                 iff every cell under its footprint has zCover < its nearest
//                 distance (per row: the max of whole 8-cell blocks + the ends).
//
// What is never an occluder: trees, the forest volume, water, structures, the
// ring, far buildings, anything that moves (§3c). Who is never culled: the
// building GLB tiles themselves (the occluders), movers.

import { sidePlanes, quatAngle } from './visframe.js';

const DEG = Math.PI / 180;

export const CDB = Object.freeze({
  azBins: 720,          // horizon azimuth bins (0.5°)
  nDist: 24,            // horizon reach bins, log-spaced 16 m … 16,384 m
  dist0: 16,
  dist1: 16384,
  r0: 16,               // the march's first radius (the disk inside it is only checked for holes)
  half: 3,              // the horizon's eye cell: ±3 m in x / z around its centre …
  yUp: 2,               // … and eyes up to 2 m over the centre's eye
  lead: 1,              // a new table starts when the eye is 1 m off the centre (or 1 m up)
  groundSlack: 1,       // the drawn ground below L0 past a node's `under` (measured AT the L0 samples): the
                        // pave cut's 0.3 m yield + the TIN's kink between samples (≤ slope · cell / 2:
                        // 0.5 m at slope 2) + float
  cutPad: 0.5,          // a bore cut volume's plan AABB grown by 0.5 m (the cut's own clearance is 0.5 mm)
  holeG: 64,            // the hole boxes' hash bucket (m)
  eyeClear: 0.5,        // the eye box's bottom over the drawn ground (groundDrawn, viscull.js _eyeAbove)
  cols: 48,             // CDB columns (rows follow the rectangle's aspect)
  rowsMin: 8,
  rowsMax: 64,
  wallNear: 200,        // collision prisms within 200 m are occluders
  erode: 0.3,           // a prism wall is inset 0.3 m into its footprint, its ends shortened alike
  wallMin: 0.5,         // a wall shorter than this after the inset is dropped
  minSide: 3,           // a footprint thinner than 3 m (bbox) is no occluder
  zNear: 0.5,           // a unit nearer than this is never hidden
  zClip: 0.05,          // the projection clips the unit at z = −zClip (camera space)
});

const HZ_LEVELS = 10;   // bin-range minima up to 512 bins (a wider range reads the all-bin min)

export const HZ_DISTS = (() => {
  const d = new Float64Array(CDB.nDist), r = Math.log(CDB.dist1 / CDB.dist0) / (CDB.nDist - 1);
  for (let j = 0; j < CDB.nDist; j++) d[j] = CDB.dist0 * Math.exp(r * j);
  return d;
})();

// the march radii: 4 m steps, then 2 % growth, to past the last reach bin
export const HZ_RADII = (() => {
  const out = [];
  for (let r = CDB.r0; r <= CDB.dist1 + CDB.half * 2; r += Math.max(4, 0.02 * r)) out.push(r);
  return Float64Array.from(out);
})();

// ---------------------------------------------------------------------------
// LowerGround

export class LowerGround {
  // cell: the sample spacing (m); ns × ms samples, sample(i, j) the height of
  // the sample at ((i + 0.5)·cell, (j + 0.5)·cell) or null (the −0.5
  // convention); W × H: the ground tree's frame (x 0 … W, z 0 … H)
  constructor(cell, ns, ms, sample, W, H) {
    this.cell = cell; this.W = W; this.H = H;
    // patch i spans x ∈ [(i + 0.5)c, (i + 1.5)c]; the patches the frame needs
    this.ni = Math.min(ns - 1, Math.ceil(W / cell - 0.5));
    this.nj = Math.min(ms - 1, Math.ceil(H / cell - 0.5));
    const ni = this.ni, nj = this.nj;
    const v = new Float32Array(ni * nj), mk = new Uint8Array(ni * nj);
    for (let j = 0; j < nj; j++) for (let i = 0; i < ni; i++) {
      const a = sample(i, j), b = sample(i + 1, j), c = sample(i, j + 1), d = sample(i + 1, j + 1);
      const k = j * ni + i;
      if (a === null || b === null || c === null || d === null) { v[k] = -Infinity; mk[k] = 2; continue; }   // bit 2: no sample
      v[k] = Math.min(a, b, c, d);
    }
    this.lv = [{ n: ni, m: nj, v, mk }];
    this.holes = [];         // [x0, z0, x1, z1] per hole
    this._hg = new Map();    // bucket (CDB.holeG) -> hole ids
    this._mip();
  }

  _mip() {
    const lv = this.lv;
    lv.length = 1;
    while (lv[lv.length - 1].n > 1 || lv[lv.length - 1].m > 1) {
      const p = lv[lv.length - 1], n = Math.ceil(p.n / 2), m = Math.ceil(p.m / 2);
      const v = new Float32Array(n * m), mk = new Uint8Array(n * m);
      for (let j = 0; j < m; j++) for (let i = 0; i < n; i++) {
        let lo = Infinity, mx = 0;
        for (let dj = 0; dj < 2; dj++) for (let di = 0; di < 2; di++) {
          const x = 2 * i + di, z = 2 * j + dj;
          if (x >= p.n || z >= p.m) continue;
          const k = z * p.n + x;
          if (p.v[k] < lo) lo = p.v[k];
          mx |= p.mk[k];
        }
        v[j * n + i] = lo; mk[j * n + i] = mx;
      }
      lv.push({ n, m, v, mk });
    }
  }

  // the bore cut's holes (corridor.js boreCut().vols: the volumes the bake
  // cut the ground tree with, groundm/bakectx.mjs cutNode): each plan AABB
  // grown by CDB.cutPad; the patches it touches are flagged (bit 1) and
  // query() tests the boxes themselves there
  maskHoles(vols) {
    const L = this.lv[0], c = this.cell, p = CDB.cutPad, G = CDB.holeG;
    for (const v of vols || []) {
      if (!v || !(v.x1 >= v.x0) || !(v.z1 >= v.z0)) continue;
      const b = [v.x0 - p, v.z0 - p, v.x1 + p, v.z1 + p], id = this.holes.length;
      this.holes.push(b);
      for (let gz = Math.floor(b[1] / G); gz <= Math.floor(b[3] / G); gz++) {
        for (let gx = Math.floor(b[0] / G); gx <= Math.floor(b[2] / G); gx++) {
          const key = gz * 65536 + gx, a = this._hg.get(key);
          if (a) a.push(id); else this._hg.set(key, [id]);
        }
      }
      const i0 = Math.max(0, Math.floor(b[0] / c - 0.5)), i1 = Math.min(L.n - 1, Math.floor(b[2] / c - 0.5));
      const j0 = Math.max(0, Math.floor(b[1] / c - 0.5)), j1 = Math.min(L.m - 1, Math.floor(b[3] / c - 0.5));
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) L.mk[j * L.n + i] |= 1;
    }
    this._mip();
  }

  // the hole boxes meeting [x0, x1] × [z0, z1] (out: an array to fill, or
  // null: true / false on the first)
  holesIn(x0, z0, x1, z1, out = null) {
    const G = CDB.holeG, H = this.holes;
    for (let gz = Math.floor(z0 / G); gz <= Math.floor(z1 / G); gz++) {
      for (let gx = Math.floor(x0 / G); gx <= Math.floor(x1 / G); gx++) {
        const a = this._hg.get(gz * 65536 + gx);
        if (!a) continue;
        for (const id of a) {
          const b = H[id];
          if (b[0] > x1 || b[2] < x0 || b[1] > z1 || b[3] < z0) continue;
          if (!out) return true;
          if (!out.includes(b)) out.push(b);
        }
      }
    }
    return out || false;
  }

  // the lower bound over the box [x0, x1] × [z0, z1], or null when the box
  // leaves the frame, meets a patch without samples or (holes) a hole
  query(x0, z0, x1, z1, holes = true) {
    const c = this.cell;
    if (x0 < 0.5 * c || z0 < 0.5 * c || x1 > this.W || z1 > this.H) return null;
    let i0 = Math.floor(x0 / c - 0.5), i1 = Math.min(this.ni - 1, Math.floor(x1 / c - 0.5));
    let j0 = Math.floor(z0 / c - 0.5), j1 = Math.min(this.nj - 1, Math.floor(z1 / c - 0.5));
    if (i1 < i0 || j1 < j0) return null;
    let l = 0;
    while ((i1 >> l) - (i0 >> l) > 1 || (j1 >> l) - (j0 >> l) > 1) l++;
    const L = this.lv[l];
    i0 >>= l; i1 >>= l; j0 >>= l; j1 >>= l;
    let lo = Infinity, h = 0;
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
      const k = j * L.n + i, m = L.mk[k];
      if (m & 2) return null;
      h |= m;
      if (L.v[k] < lo) lo = L.v[k];
    }
    if (h && holes && this.holesIn(x0, z0, x1, z1)) return null;
    return lo;
  }
}

// the lower ground of the client ground's envelope (ground.js ClientGround
// `env`: per GM L0 node its lowest vertex, groundm/index.json y0 − 1 mm) over
// the frame W × H — sample (i, j) = node (i, j) at its centre, cell 128 m: a
// patch between four node centres lies inside those four nodes, so their
// least y0 is under every triangle there (rearch-r8k: was the coarse terrain
// level's 16 m envelope). `src` = viscull.js's `groundLower` dependency
// (main.js: `env` non-null once the index is in).
export function lowerGroundFromNodes(src, W, H) {
  const E = src.env;
  const sample = (i, j) => { const v = E.y0[j * E.nx + i]; return v === v ? v : null; };
  return new LowerGround(E.step, E.nx, E.ny, sample, W, H);
}

// ---------------------------------------------------------------------------
// HorizonTable

// the xz bbox of the annular sector r ∈ [rA, rB], azimuth a ∈ [aA, aB]
// (a = atan2(dx, dz): x = sin a, z = cos a) around (cx, cz)
function sectorBox(cx, cz, rA, rB, aA, aB, out) {
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  const put = (r, a) => {
    const x = cx + r * Math.sin(a), z = cz + r * Math.cos(a);
    if (x < x0) x0 = x; if (x > x1) x1 = x; if (z < z0) z0 = z; if (z > z1) z1 = z;
  };
  put(rA, aA); put(rA, aB); put(rB, aA); put(rB, aB);
  // an axis direction inside the span adds its outer point
  for (let k = -4; k <= 4; k++) {
    const a = k * Math.PI / 2;
    if (a > aA && a < aB) put(rB, a);
  }
  out[0] = x0; out[1] = z0; out[2] = x1; out[3] = z1;
  return out;
}

// can a ray from an eye of the cell (±CDB.half around (cx, cz)) with its
// azimuth in [a0, a1] meet one of the boxes before it leaves the first
// radius (reach T = r0 + the cell's corner radius)? The region (the cell's
// square ⊕ the wedge, convex) against each box by separating axes x, z and
// the wedge's mid normal, padded by T · (a1 − a0) for the arc: sound (an
// axis that separates proves no hit), conservative otherwise
function innerHit(cx, cz, a0, a1, boxes) {
  const T = CDB.r0 + CDB.half * Math.SQRT2, h = CDB.half, e = T * (a1 - a0) + 0.01, am = (a0 + a1) / 2;
  const ux0 = Math.sin(a0), uz0 = Math.cos(a0), ux1 = Math.sin(a1), uz1 = Math.cos(a1);
  const N = [1, 0, 0, 1, Math.cos(am), -Math.sin(am)];
  for (const b of boxes) {
    let sep = false;
    for (let k = 0; k < 6 && !sep; k += 2) {
      const nx = N[k], nz = N[k + 1], p0 = ux0 * nx + uz0 * nz, p1 = ux1 * nx + uz1 * nz;
      const o = cx * nx + cz * nz, s = h * (Math.abs(nx) + Math.abs(nz));
      const lo = o - s + T * Math.min(0, p0, p1) - e, hi = o + s + T * Math.max(0, p0, p1) + e;
      const bc = (b[0] + b[2]) / 2 * nx + (b[1] + b[3]) / 2 * nz, br = (b[2] - b[0]) / 2 * Math.abs(nx) + (b[3] - b[1]) / 2 * Math.abs(nz);
      sep = bc + br < lo || bc - br > hi;
    }
    if (!sep) return true;
  }
  return false;
}

export class HorizonTable {
  constructor(ground) {
    this.ground = ground;
    this.t = new Float32Array(CDB.azBins * CDB.nDist);
    this.ready = false;      // complete and usable
    this.off = false;        // complete, but the eye cell itself meets a hole / no samples / the frame edge
    this.version = 0;        // +1 per completed table
    this.c = null;           // [x, yHi, z]: the cell's centre and its eyes' top
    this._job = null;
    this._box = [0, 0, 0, 0];
  }

  // a new table for the cell around (x, z) with eyes up to yHi; margin[i]
  // the drawn ground's undershoot below the lower ground at HZ_RADII[i]
  // (Infinity = no bound there). The previous table stays usable until done.
  start(x, yHi, z, margin) {
    const hT = CDB.half * Math.SQRT2, r = CDB.r0 + hT, h = CDB.half, G = this.ground;
    // the holes within the first radius: per bin (step, innerHit)
    const near = G.holesIn(x - r, z - r, x + r, z + r, []);
    this._job = { c: [x, yHi, z], margin: Float32Array.from(margin), b: 0, l: 0, lv: [], t: new Float32Array(CDB.azBins * CDB.nDist), off: false, near };
    const J = this._job;
    if (G.query(x - r, z - r, x + r, z + r, false) === null || near.some((b) => b[0] <= x + h && b[2] >= x - h && b[1] <= z + h && b[3] >= z - h)) {
      J.off = true; J.b = CDB.azBins; J.t.fill(-Infinity);
    }
  }

  get building() { return !!this._job; }

  // work for at most `ms`; true when a table completed in this call
  step(ms, now = () => performance.now()) {
    const J = this._job;
    if (!J) return false;
    const t0 = now(), hT = CDB.half * Math.SQRT2, D = HZ_DISTS, R = HZ_RADII, nb = CDB.azBins, nd = CDB.nDist;
    const da = 2 * Math.PI / nb, [cx, yHi, cz] = J.c, box = this._box, G = this.ground;
    while (J.b < nb) {
      const b = J.b++, a0 = -Math.PI + b * da, a1 = a0 + da, row = b * nd;
      let run = -Infinity, j = 0, rPrev = CDB.r0;
      // a hole the cell's rays can reach inside the first radius: no bound
      const R0 = J.near.length && innerHit(cx, cz, a0, a1, J.near) ? 0 : R.length;
      for (let i = 0; i < R0; i++) {
        const rho = R[i], reach = rho + hT;
        while (j < nd && D[j] < reach) J.t[row + j++] = run;
        if (j >= nd) break;
        const dl = Math.asin(Math.min(1, hT / rPrev));
        sectorBox(cx, cz, rPrev, rho, a0 - dl, a1 + dl, box);
        const g0 = G.query(box[0], box[1], box[2], box[3]);
        if (g0 === null) break;                       // a hole / the frame edge: frozen
        const g = g0 - J.margin[i];
        if (g > -Infinity) {
          const tn = g >= yHi ? (g - yHi) / (rho + hT) : (g - yHi) / (rho - hT);
          if (tn > run) run = tn;
        }
        rPrev = rho;
      }
      while (j < nd) J.t[row + j++] = run;
      if (now() - t0 >= ms) return false;
    }
    // the bin-range minima (ConservativeDepth._terrain): level l holds, per
    // bin b of the doubled ring (2 · azBins) and reach j, the min over bins
    // b … b + 2^l − 1; one level per pass
    while (J.l < HZ_LEVELS) {
      const n2 = 2 * nb * nd;
      if (J.l === 0) {
        const a = new Float32Array(n2);
        a.set(J.t, 0); a.set(J.t, nb * nd);
        J.lv.push(a);
      } else {
        const p = J.lv[J.l - 1], a = new Float32Array(n2), w = (1 << (J.l - 1)) * nd;
        for (let k = 0; k + w < n2; k++) a[k] = p[k] < p[k + w] ? p[k] : p[k + w];
        J.lv.push(a);
      }
      J.l++;
      if (now() - t0 >= ms) return false;
    }
    const all = new Float32Array(nd).fill(Infinity);
    for (let b = 0; b < nb; b++) for (let j = 0; j < nd; j++) if (J.t[b * nd + j] < all[j]) all[j] = J.t[b * nd + j];
    this.lv = J.lv; this.all = all;
    this.t = J.t; this.c = J.c; this.margin = J.margin; this.off = J.off;
    this.ready = true; this.version++;
    this._job = null;
    return true;
  }

  // the min of the table over bins b0 … b1 (any integers, b0 ≤ b1) at reach j
  minT(b0, b1, j) {
    const nb = CDB.azBins, nd = CDB.nDist;
    if (b1 - b0 + 1 >= nb) return this.all[j];
    const s = Math.floor(b0 / nb) * nb;
    b0 -= s; b1 -= s;
    const l = 31 - Math.clz32(b1 - b0 + 1), a = this.lv[l];
    const u = a[b0 * nd + j], v = a[(b1 - (1 << l) + 1) * nd + j];
    return u < v ? u : v;
  }

  // the table serves this eye box (lo / hi) and margins that are no larger
  covers(lo, hi) {
    if (!this.ready || this.off) return false;
    const [cx, yHi, cz] = this.c, h = CDB.half;
    return lo[0] >= cx - h && hi[0] <= cx + h && lo[2] >= cz - h && hi[2] <= cz + h && hi[1] <= yHi;
  }
}

// M(ρ) per HZ_RADII from a drawn node list: [{x0, z0, x1, z1, under}] —
// the largest undershoot of any drawn node an annulus meets (+ the slack);
// Infinity where no node lies
export function horizonMargin(cx, cz, nodes) {
  const R = HZ_RADII, m = new Float32Array(R.length).fill(Infinity), seen = new Uint8Array(R.length);
  const hT = CDB.half * Math.SQRT2;
  for (const n of nodes) {
    const dx = Math.max(n.x0 - cx, 0, cx - n.x1), dz = Math.max(n.z0 - cz, 0, cz - n.z1);
    const dmin = Math.max(0, Math.hypot(dx, dz) - hT);
    const fx = Math.max(Math.abs(n.x0 - cx), Math.abs(n.x1 - cx)), fz = Math.max(Math.abs(n.z0 - cz), Math.abs(n.z1 - cz));
    const dmax = Math.hypot(fx, fz) + hT;
    const u = n.under + CDB.groundSlack;
    for (let i = 0; i < R.length; i++) {
      const rA = i ? R[i - 1] : 0;
      if (R[i] < dmin || rA > dmax) continue;
      if (!seen[i] || u > m[i]) { m[i] = u; seen[i] = 1; }
    }
  }
  return m;
}

// ---------------------------------------------------------------------------
// prism walls

// the inset walls of one collision item ({fp: rings [[x, z]…], y0, roof: tris
// [[x, y, z]×3], fp_src}) — [] when it is no occluder: a roof-only plan
// (`fp_src 'roof'`), no roof (y1 is the apex, the eave unknown), a footprint
// thinner than CDB.minSide, or under CDB.wallMin of height
export function itemWalls(b, erode = CDB.erode, out = []) {
  if (!b || b.fp_src === 'roof' || !b.roof || !b.roof.length || !b.fp || !b.fp.length) return out;
  let eave = Infinity;
  for (const t of b.roof) for (const p of t) if (p[1] < eave) eave = p[1];
  const y0 = b.y0;
  if (!(eave - y0 >= CDB.wallMin)) return out;
  for (const ring of b.fp) {
    const n = ring.length;
    if (n < 3) continue;
    let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity, area = 0;
    for (let i = 0; i < n; i++) {
      const [ax, az] = ring[i], [bx, bz] = ring[(i + 1) % n];
      area += ax * bz - bx * az;
      if (ax < x0) x0 = ax; if (ax > x1) x1 = ax; if (az < z0) z0 = az; if (az > z1) z1 = az;
    }
    if (x1 - x0 < CDB.minSide || z1 - z0 < CDB.minSide || area === 0) continue;
    const s = area > 0 ? 1 : -1;      // the inward normal of edge (dx, dz) is s · (−dz, dx)
    // the end shortening at vertex i: erode / tan(α / 2) at a convex corner, erode at a reflex one
    const cut = (i) => {
      const [px, pz] = ring[(i - 1 + n) % n], [qx, qz] = ring[i], [rx, rz] = ring[(i + 1) % n];
      const ux = px - qx, uz = pz - qz, vx = rx - qx, vz = rz - qz;
      const lu = Math.hypot(ux, uz), lv = Math.hypot(vx, vz);
      if (!lu || !lv) return Infinity;
      const cr = s * (vx * uz - vz * ux);        // > 0: convex (interior angle < 180°)
      if (cr <= 0) return erode;
      const ang = Math.acos(Math.max(-1, Math.min(1, (ux * vx + uz * vz) / (lu * lv))));
      return ang < 1e-3 ? Infinity : erode / Math.tan(ang / 2);
    };
    for (let i = 0; i < n; i++) {
      const [ax, az] = ring[i], [bx, bz] = ring[(i + 1) % n];
      const dx = bx - ax, dz = bz - az, L = Math.hypot(dx, dz);
      if (!L) continue;
      const ca = cut(i), cb = cut((i + 1) % n);
      if (L - ca - cb < CDB.wallMin) continue;
      const ex = dx / L, ez = dz / L, nx = -s * ez, nz = s * ex;
      out.push({
        ax: ax + ex * ca + nx * erode, az: az + ez * ca + nz * erode,
        bx: bx - ex * cb + nx * erode, bz: bz - ez * cb + nz * erode,
        y0, y1: eave,
      });
    }
  }
  return out;
}

// is (x, z) inside any fp ring of the item, or within `pad` of one
export function nearFootprint(b, x, z, pad) {
  for (const ring of b.fp || []) {
    const n = ring.length;
    let inside = false;
    for (let i = 0, k = n - 1; i < n; k = i++) {
      const [ax, az] = ring[i], [bx, bz] = ring[k];
      if ((az > z) !== (bz > z) && x < ax + (z - az) * (bx - ax) / (bz - az)) inside = !inside;
      const dx = bx - ax, dz = bz - az, L2 = dx * dx + dz * dz || 1e-12;
      const t = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / L2));
      if (Math.hypot(ax + t * dx - x, az + t * dz - z) <= pad) return true;
    }
    if (inside) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// the view rectangle

// the key camera's tangent-space rectangle [X0, X1, Y0, Y1] that holds every
// view direction of every pose of the bound: the side planes of each
// projection (the key's; VR: + each eye's, with its rotation from the key
// added to er) dilated like visframe.js dilatedPlanes (n' = n + k·f), each
// an axis line in X = x / −z, Y = y / −z. A projection with skewed side
// planes throws (no silent wrong bound).
export function viewRect(key, eyes = null) {
  const r = [Infinity, -Infinity, Infinity, -Infinity];
  const add = (proj, er) => {
    const { planes, half } = sidePlanes(proj);
    const th = Math.min(half + er, 89 * DEG), k = er > 0 ? 2 * Math.sin(er / 2) / Math.cos(th) : 0;
    for (const [nx, ny, nz0] of planes) {
      const nz = nz0 - k;     // n + k·(0, 0, −1); the inside: nx X + ny Y − nz ≥ 0
      if (Math.abs(ny) < 1e-9 * Math.hypot(nx, nz) && Math.abs(nx) > 1e-9) {
        const X = nz / nx;
        if (nx > 0) r[0] = Math.min(r[0], X); else r[1] = Math.max(r[1], X);
      } else if (Math.abs(nx) < 1e-9 * Math.hypot(ny, nz) && Math.abs(ny) > 1e-9) {
        const Y = nz / ny;
        if (ny > 0) r[2] = Math.min(r[2], Y); else r[3] = Math.max(r[3], Y);
      } else throw new Error('conservativedepth: a skewed side plane (no axis rectangle)');
    }
  };
  add(key.proj, key.er);
  for (const e of eyes || []) add(e.proj, key.er + quatAngle(key.quat, e.quat));
  if (!(r[0] < r[1] && r[2] < r[3])) throw new Error('conservativedepth: an empty view rectangle');
  return r;
}

// ---------------------------------------------------------------------------
// ConservativeDepth

export class ConservativeDepth {
  constructor() {
    this.cols = 0; this.rows = 0;
    this.z = null;           // Float32 rows × cols: zCover
    this.bm = null;          // per row maxima over 8-cell blocks
    this.M = new Float64Array(9);   // world → key camera (the inverse of key.quat)
    this._pc = new Float64Array(24);
    this.stats = { terrain: 0, walls: 0, wallCells: 0, wallsSkipped: 0 };
  }

  // key: {lo, hi, quat} (visframe key); rect: viewRect; horizon: a table
  // that covers the key's eye box (or null); walls: itemWalls() quads
  build(key, rect, horizon, walls) {
    const cols = CDB.cols;
    const rows = Math.max(CDB.rowsMin, Math.min(CDB.rowsMax, Math.round(cols * (rect[3] - rect[2]) / (rect[1] - rect[0]))));
    this.cols = cols; this.rows = rows; this.rect = rect;
    this.dX = (rect[1] - rect[0]) / cols; this.dY = (rect[3] - rect[2]) / rows;
    const nv = (cols + 1) * (rows + 1);
    if (!this.z || this.z.length !== cols * rows) {
      this.z = new Float32Array(cols * rows);
      this.vd = new Float32Array(3 * nv);
      this.vt = new Float32Array(nv); this.vs = new Float32Array(nv); this.vm = new Float32Array(nv);
      this.vk = new Int32Array(nv); this._stamp = 0;
    }
    const z = this.z, vd = this.vd, q = key.quat;
    z.fill(Infinity);
    this.c = [(key.lo[0] + key.hi[0]) / 2, (key.lo[1] + key.hi[1]) / 2, (key.lo[2] + key.hi[2]) / 2];
    this.h = [(key.hi[0] - key.lo[0]) / 2, (key.hi[1] - key.lo[1]) / 2, (key.hi[2] - key.lo[2]) / 2];
    this.hn = Math.sqrt(this.h[0] * this.h[0] + this.h[1] * this.h[1] + this.h[2] * this.h[2]);
    // a point clipped behind z = −zClip inside the rectangle is within
    // zClip·√(1 + X² + Y²) of the eye: a unit nearer than that stays in
    const Xm = Math.max(-rect[0], rect[1]), Ym = Math.max(-rect[2], rect[3]);
    this.nearMin = Math.max(CDB.zNear, CDB.zClip * Math.sqrt(1 + Xm * Xm + Ym * Ym));
    // the rotation matrix R of q (columns: the camera axes in world); M = Rᵀ
    // row-major (M[3i …] = column i of R)
    const [qx, qy, qz, qw] = q, M = this.M;
    const R00 = 1 - 2 * (qy * qy + qz * qz), R01 = 2 * (qx * qy - qz * qw), R02 = 2 * (qx * qz + qy * qw);
    const R10 = 2 * (qx * qy + qz * qw), R11 = 1 - 2 * (qx * qx + qz * qz), R12 = 2 * (qy * qz - qx * qw);
    const R20 = 2 * (qx * qz - qy * qw), R21 = 2 * (qy * qz + qx * qw), R22 = 1 - 2 * (qx * qx + qy * qy);
    M[0] = R00; M[1] = R10; M[2] = R20; M[3] = R01; M[4] = R11; M[5] = R21; M[6] = R02; M[7] = R12; M[8] = R22;
    // the grid vertices' world directions (unit)
    for (let r = 0, k = 0; r <= rows; r++) {
      const Y = rect[2] + r * this.dY;
      for (let c = 0; c <= cols; c++, k += 3) {
        const X = rect[0] + c * this.dX, il = 1 / Math.sqrt(X * X + Y * Y + 1);
        const x = X * il, y = Y * il, w = -il;
        vd[k] = R00 * x + R01 * y + R02 * w; vd[k + 1] = R10 * x + R11 * y + R12 * w; vd[k + 2] = R20 * x + R21 * y + R22 * w;
      }
    }
    this.stats.terrain = 0; this.stats.walls = 0; this.stats.wallCells = 0; this.stats.wallsSkipped = 0;
    if (horizon) this._terrain(horizon);
    if (walls && walls.length) this._walls(walls);
    this._tables();
    return this;
  }

  // the terrain per block of TB × TB cells: the block's cone (its centre ±
  // the corner radius: a cap holds the great-circle quad of its corners) is
  // covered at the first reach whose every bin blocks the cone's top (the
  // table is monotone in reach: binary search)
  _terrain(H) {
    const { cols, rows, vd, z } = this, nb = CDB.azBins, nd = CDB.nDist, da = 2 * Math.PI / nb, D = HZ_DISTS;
    const W = cols + 1, lim = 85 * DEG;
    let n = 0;
    for (let r = 0; r < rows; r += TB) {
      const r1 = Math.min(rows, r + TB);
      for (let c = 0; c < cols; c += TB) {
        const c1 = Math.min(cols, c + TB);
        const k0 = 3 * (r * W + c), k1 = 3 * (r * W + c1), k2 = 3 * (r1 * W + c), k3 = 3 * (r1 * W + c1);
        let mx = vd[k0] + vd[k1] + vd[k2] + vd[k3], my = vd[k0 + 1] + vd[k1 + 1] + vd[k2 + 1] + vd[k3 + 1], mz = vd[k0 + 2] + vd[k1 + 2] + vd[k2 + 2] + vd[k3 + 2];
        const il = 1 / Math.sqrt(mx * mx + my * my + mz * mz);
        mx *= il; my *= il; mz *= il;
        const dmin = Math.min(mx * vd[k0] + my * vd[k0 + 1] + mz * vd[k0 + 2], mx * vd[k1] + my * vd[k1 + 1] + mz * vd[k1 + 2],
          mx * vd[k2] + my * vd[k2 + 1] + mz * vd[k2 + 2], mx * vd[k3] + my * vd[k3 + 1] + mz * vd[k3 + 2]);
        // a hair over the corner radius: float rounding never shrinks the cone
        const rho = Math.acos(Math.max(-1, Math.min(1, dmin))) * (1 + 1e-6) + 1e-9;
        const el = Math.asin(Math.max(-1, Math.min(1, my)));
        if (Math.abs(el) + rho >= lim) continue;
        const tMax = Math.tan(el + rho);
        const az = Math.atan2(mx, mz), daz = Math.asin(Math.min(1, Math.sin(rho) / Math.cos(el)));
        const b0 = Math.floor((az - daz + Math.PI) / da), b1 = Math.floor((az + daz + Math.PI) / da);
        // the farthest reach first: no bin blocks there → not covered
        if (!(tMax < H.minT(b0, b1, nd - 1))) continue;
        let lo = 0, hi = nd - 1;
        while (lo < hi) { const j = (lo + hi) >> 1; if (tMax < H.minT(b0, b1, j)) hi = j; else lo = j + 1; }
        const zc = D[lo] / Math.cos(Math.max(Math.abs(el - rho), Math.abs(el + rho)));
        for (let rr = r; rr < r1; rr++) for (let cc = c; cc < c1; cc++) {
          const k = rr * cols + cc;
          if (zc < z[k]) z[k] = zc;
          n++;
        }
      }
    }
    this.stats.terrain = n;
  }

  // the walls in view nearest first; a wall whose cells are already covered
  // nearer than its plane is skipped (it can only cover farther)
  _walls(walls) {
    const c = this.c, P = this._pc;
    let ord = this._ord, fr = this._fr, n = 0;
    // sized per wall (ord 2, fr 4): a typed array drops a write past its end
    // and reads undefined back — `ord.length < walls.length` regrew only past
    // 2× and a reused instance threw on _wall(undefined)
    if (!ord || ord.length < 2 * walls.length) { ord = this._ord = new Float64Array(2 * walls.length); fr = this._fr = new Int32Array(4 * walls.length); }
    for (let i = 0; i < walls.length; i++) {
      const w = walls[i];
      this._cam(0, w.ax, w.y0, w.az); this._cam(1, w.bx, w.y0, w.bz); this._cam(2, w.ax, w.y1, w.az); this._cam(3, w.bx, w.y1, w.bz);
      const f = this._footprint(P, 4, QUAD_EDGES);
      if (!f) continue;
      const c0 = Math.max(0, f[0]), c1 = Math.min(this.cols, f[1] + 1), r0 = Math.max(0, f[2]), r1 = Math.min(this.rows, f[3] + 1);
      if (c0 >= c1 || r0 >= r1) continue;
      const ex = w.bx - w.ax, ez = w.bz - w.az, L2 = ex * ex + ez * ez;
      const t = L2 > 0 ? Math.max(0, Math.min(1, ((c[0] - w.ax) * ex + (c[2] - w.az) * ez) / L2)) : 0;
      const dx = w.ax + t * ex - c[0], dz = w.az + t * ez - c[2];
      ord[2 * n] = dx * dx + dz * dz; ord[2 * n + 1] = i;
      fr[4 * i] = c0; fr[4 * i + 1] = c1; fr[4 * i + 2] = r0; fr[4 * i + 3] = r1;
      n++;
    }
    const idx = this._idx || (this._idx = []);
    idx.length = n;
    for (let k = 0; k < n; k++) idx[k] = 2 * k;
    idx.sort((a, b) => ord[a] - ord[b]);
    for (let k = 0; k < n; k++) {
      const i = ord[idx[k] + 1];
      this._wall(walls[i], fr[4 * i], fr[4 * i + 1], fr[4 * i + 2], fr[4 * i + 3]);
    }
  }

  // one vertical wall quad {ax, az, bx, bz, y0, y1} over its cell range
  // [c0, c1) × [r0, r1)
  _wall(w, c0, c1, r0, r1) {
    const { cols, vd, vt, vs, vm, vk, z, c, hn } = this, W = cols + 1;
    const ex = w.bx - w.ax, ez = w.bz - w.az, L = Math.sqrt(ex * ex + ez * ez);
    if (!L) return;
    const ux = ex / L, uz = ez / L, nx = -uz, nz = ux;            // the plane's normal (either side)
    const off = nx * (w.ax - c[0]) + nz * (w.az - c[2]);          // n·(a − c)
    if (Math.abs(off) < 1e-6) return;                             // the eye on the wall's plane
    // occluded: 3 × 3 cells across its range covered nearer than its plane
    // (a skip only drops an occluder: never a wrong cull)
    const dW = Math.abs(off);
    let open = false;
    for (let a = 0; a < 3 && !open; a++) for (let b = 0; b < 3; b++) {
      const r = r0 + (((r1 - 1 - r0) * a) >> 1), cc = c0 + (((c1 - 1 - c0) * b) >> 1);
      if (z[r * cols + cc] > dW) { open = true; break; }
    }
    if (!open) { this.stats.wallsSkipped++; return; }
    this.stats.walls++;
    // a vertex's ray against the plane, computed once per wall (stamp) and
    // only for cells the wall can still improve (z > its plane distance)
    const stamp = ++this._stamp, cx = c[0], cy = c[1], cz = c[2], y0 = w.y0, y1 = w.y1, ax = w.ax, az = w.az;
    let n = 0;
    for (let r = r0; r < r1; r++) for (let cc = c0; cc < c1; cc++) {
      const k = r * cols + cc;
      if (!(z[k] > dW)) continue;
      const v0 = r * W + cc;
      let ok = true;
      for (let q = 0; q < 4; q++) {
        const v = v0 + (q & 1) + (q & 2 ? W : 0);
        if (vk[v] !== stamp) {
          vk[v] = stamp; vt[v] = -1;
          const kk = 3 * v, nd = nx * vd[kk] + nz * vd[kk + 2];
          if (nd > 1e-9 || nd < -1e-9) {
            const t = off / nd;
            if (t > 0) {
              const py = cy + t * vd[kk + 1], u = (cx + t * vd[kk] - ax) * ux + (cz + t * vd[kk + 2] - az) * uz;
              vt[v] = t;
              vs[v] = Math.min(u, L - u, py - y0, y1 - py);
              vm[v] = hn * (1 + 1 / Math.abs(nd));
            }
          }
        }
        if (!(vt[v] > 0)) { ok = false; break; }
      }
      if (!ok) continue;
      const v1 = v0 + 1, v2 = v0 + W, v3 = v2 + 1;
      const s = Math.min(vs[v0], vs[v1], vs[v2], vs[v3]), m = Math.max(vm[v0], vm[v1], vm[v2], vm[v3]);
      if (!(s >= m)) continue;
      const zc = Math.max(vt[v0], vt[v1], vt[v2], vt[v3]) + m + hn;
      if (zc < z[k]) z[k] = zc;
      n++;
    }
    this.stats.wallCells += n;
  }

  // per row maxima of zCover over blocks of 8 cells (the tests' long runs)
  _tables() {
    const { cols, rows, z } = this, nb = (cols + 7) >> 3;
    if (!this.bm || this.bm.length !== rows * nb) this.bm = new Float32Array(rows * nb);
    const bm = this.bm;
    for (let r = 0; r < rows; r++) for (let b = 0; b < nb; b++) {
      let m = -Infinity;
      for (let c = b << 3, e = Math.min(cols, c + 8), k = r * cols + c; c < e; c++, k++) if (z[k] > m) m = z[k];
      bm[r * nb + b] = m;
    }
  }

  // every cell of row r over [c0, c1] covered nearer than d
  _row(r, c0, c1, d) {
    const z = this.z, o = r * this.cols;
    if (c1 - c0 < 16) {
      for (let k = o + c0, e = o + c1; k <= e; k++) if (!(z[k] < d)) return false;
      return true;
    }
    const bm = this.bm, ob = r * ((this.cols + 7) >> 3), b0 = (c0 + 7) >> 3, b1 = (c1 + 1) >> 3;   // whole blocks b0 … b1 − 1
    for (let k = o + c0, e = o + (b0 << 3); k < e; k++) if (!(z[k] < d)) return false;
    for (let b = b0; b < b1; b++) if (!(bm[ob + b] < d)) return false;
    for (let k = o + (b1 << 3), e = o + c1; k <= e; k++) if (!(z[k] < d)) return false;
    return true;
  }

  // world point (x, y, z) into the key camera's frame (from the box centre) → _pc[3i …]
  _cam(i, x, y, z) {
    const M = this.M, P = this._pc, c = this.c, dx = x - c[0], dy = y - c[1], dz = z - c[2];
    P[3 * i] = M[0] * dx + M[1] * dy + M[2] * dz;
    P[3 * i + 1] = M[3] * dx + M[4] * dy + M[5] * dz;
    P[3 * i + 2] = M[6] * dx + M[7] * dy + M[8] * dz;
  }

  // the grid range [c0, c1, r0, r1] (cells, unclamped) under the clipped
  // projection of the n camera-space points P (joined by `edges`); null when
  // nothing lies in front of the eye
  _footprint(P, n, edges) {
    const zc = -CDB.zClip;
    let X0 = Infinity, X1 = -Infinity, Y0 = Infinity, Y1 = -Infinity, any = false, all = true;
    for (let i = 0; i < n; i++) {
      const zz = P[3 * i + 2];
      if (zz > zc) { all = false; continue; }
      const X = P[3 * i] / -zz, Y = P[3 * i + 1] / -zz;
      if (X < X0) X0 = X; if (X > X1) X1 = X; if (Y < Y0) Y0 = Y; if (Y > Y1) Y1 = Y;
      any = true;
    }
    if (!all) {
      for (let e = 0; e < edges.length; e += 2) {
        const a = 3 * edges[e], b = 3 * edges[e + 1];
        if ((P[a + 2] <= zc) === (P[b + 2] <= zc)) continue;
        const t = (zc - P[a + 2]) / (P[b + 2] - P[a + 2]);
        const X = (P[a] + t * (P[b] - P[a])) / -zc, Y = (P[a + 1] + t * (P[b + 1] - P[a + 1])) / -zc;
        if (X < X0) X0 = X; if (X > X1) X1 = X; if (Y < Y0) Y0 = Y; if (Y > Y1) Y1 = Y;
        any = true;
      }
    }
    if (!any) return null;
    const R = this.rect, f = this._fp || (this._fp = [0, 0, 0, 0]);
    f[0] = Math.floor((X0 - R[0]) / this.dX); f[1] = Math.floor((X1 - R[0]) / this.dX);
    f[2] = Math.floor((Y0 - R[2]) / this.dY); f[3] = Math.floor((Y1 - R[2]) / this.dY);
    return f;
  }

  // 'hidden' | 'visible' | 'out' (outside the view rectangle) for the box
  // [lo, hi] (world, the unit's upper bound)
  test(lo, hi) {
    const c = this.c, h = this.h;
    const x0 = lo[0] - h[0], y0 = lo[1] - h[1], z0 = lo[2] - h[2], x1 = hi[0] + h[0], y1 = hi[1] + h[1], z1 = hi[2] + h[2];
    const dx = Math.max(x0 - c[0], 0, c[0] - x1), dy = Math.max(y0 - c[1], 0, c[1] - y1), dz = Math.max(z0 - c[2], 0, c[2] - z1);
    const dNear = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dNear < this.nearMin) return 'visible';
    // the 8 corners in the camera frame: the base corner + the rotated edges
    const M = this.M, P = this._pc, bx = x0 - c[0], by = y0 - c[1], bz = z0 - c[2], sx = x1 - x0, sy = y1 - y0, sz = z1 - z0;
    const p0 = M[0] * bx + M[1] * by + M[2] * bz, p1 = M[3] * bx + M[4] * by + M[5] * bz, p2 = M[6] * bx + M[7] * by + M[8] * bz;
    const ax0 = M[0] * sx, ax1 = M[3] * sx, ax2 = M[6] * sx, ay0 = M[1] * sy, ay1 = M[4] * sy, ay2 = M[7] * sy;
    const az0 = M[2] * sz, az1 = M[5] * sz, az2 = M[8] * sz;
    P[0] = p0; P[1] = p1; P[2] = p2;
    P[3] = p0 + ax0; P[4] = p1 + ax1; P[5] = p2 + ax2;
    P[6] = p0 + ay0; P[7] = p1 + ay1; P[8] = p2 + ay2;
    P[9] = P[3] + ay0; P[10] = P[4] + ay1; P[11] = P[5] + ay2;
    for (let k = 0; k < 12; k++) P[12 + k] = P[k] + (k % 3 === 0 ? az0 : k % 3 === 1 ? az1 : az2);
    const zc = -CDB.zClip;
    // all corners in front: their projections; else the clipped footprint
    let X0 = Infinity, X1 = -Infinity, Y0 = Infinity, Y1 = -Infinity, front = true;
    for (let k = 0; k < 24; k += 3) {
      if (!(P[k + 2] <= zc)) { front = false; break; }
      const r = -1 / P[k + 2], X = P[k] * r, Y = P[k + 1] * r;
      if (X < X0) X0 = X; if (X > X1) X1 = X; if (Y < Y0) Y0 = Y; if (Y > Y1) Y1 = Y;
    }
    let fc0, fc1, fr0, fr1;
    if (front) {
      const R = this.rect;
      fc0 = Math.floor((X0 - R[0]) / this.dX); fc1 = Math.floor((X1 - R[0]) / this.dX);
      fr0 = Math.floor((Y0 - R[2]) / this.dY); fr1 = Math.floor((Y1 - R[2]) / this.dY);
    } else {
      const f = this._footprint(P, 8, BOX_EDGES);
      if (!f) return 'out';
      fc0 = f[0]; fc1 = f[1]; fr0 = f[2]; fr1 = f[3];
    }
    const c0 = Math.max(0, fc0), c1 = Math.min(this.cols - 1, fc1), r0 = Math.max(0, fr0), r1 = Math.min(this.rows - 1, fr1);
    if (c0 > c1 || r0 > r1) return 'out';
    for (let r = r0; r <= r1; r++) if (!this._row(r, c0, c1, dNear)) return 'visible';
    return 'hidden';
  }
}

const TB = 2;   // the terrain's block (cells per side)
const QUAD_EDGES = [0, 1, 2, 3, 0, 2, 1, 3];
const BOX_EDGES = [0, 1, 2, 3, 4, 5, 6, 7, 0, 2, 1, 3, 4, 6, 5, 7, 0, 4, 1, 5, 2, 6, 3, 7];
